import React from 'react';
import { Check, Music, BookOpen, Star, ArrowRight, ShieldCheck, Gift, X } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
export function SalesPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const testimonials = [{
    name: 'Patricia Gómez',
    age: 34,
    role: 'Maestra de nivel inicial',
    text: 'Como docente sin formación musical, siempre sentí inseguridad al enseñar música. Este kit cambió todo. Ahora tengo actividades claras, progresivas y mis alumnos están más motivados que nunca. Ya no improviso, sigo un sistema que realmente funciona.'
  }, {
    name: 'Roberto Fernández',
    age: 41,
    role: 'Padre de dos niños',
    text: 'Quería que mis hijos aprendieran música en casa pero no sabía por dónde empezar. Con este kit, las tardes se convirtieron en momentos musicales increíbles. Ellos disfrutan, aprenden y yo me siento seguro guiándolos sin ser músico.'
  }, {
    name: 'Claudia Morales',
    age: 29,
    role: 'Tallerista comunitaria',
    text: 'Trabajo con grupos de niños en un centro cultural y este material fue un salvavidas. Las actividades son prácticas, divertidas y no necesito saber música para aplicarlas. Los niños piden más y los padres notan el progreso.'
  }, {
    name: 'Miguel Ángel Torres',
    age: 38,
    role: 'Docente de primaria',
    text: 'Implementé el kit en mi clase de 25 alumnos y los resultados fueron sorprendentes. La estructura es tan clara que todos avanzan al mismo ritmo. Dejé de sentir que la música era un relleno y ahora es una de las actividades más esperadas.'
  }, {
    name: 'Sofía Ramírez',
    age: 32,
    role: 'Madre y educadora en casa',
    text: 'Educo a mis hijos en casa y la música siempre fue mi punto débil. Este kit me dio la confianza y las herramientas que necesitaba. Ahora la música es parte natural de nuestro día y veo cómo mis hijos desarrollan habilidades reales.'
  }];
  return <div className="min-h-screen bg-amber-50 font-sans text-gray-800 selection:bg-teal-200 selection:text-teal-900" data-id="element-20">
      <PurchaseNotification data-id="element-21" />

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-teal-50 to-amber-50 pt-12 pb-20 px-4 md:px-8" data-id="element-22">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-23">
          <div className="order-2 md:order-1 space-y-6" data-id="element-24">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-bold mb-2" data-id="element-25">
              <Star className="w-4 h-4 mr-1 fill-current" data-id="element-26" />
              Método probado para enseñar música
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-teal-900 leading-tight" data-id="element-27">
              Enseñar música a niños no debería depender de improvisar…{' '}
              <span className="text-orange-500" data-id="element-28">ni de saber música</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed" data-id="element-29">
              La guía definitiva para padres y docentes que quieren introducir
              la música de forma estructurada, lúdica y sin estrés.
            </p>
            <div className="pt-4" data-id="element-30">
              <button onClick={scrollToPurchase} className="bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center gap-2 w-full md:w-auto justify-center" data-id="element-31">
                Quiero el Kit Completo
                <ArrowRight className="w-6 h-6" data-id="element-32" />
              </button>
            </div>
          </div>
          <div className="order-1 md:order-2 flex justify-center" data-id="element-33">
            <div className="relative" data-id="element-34">
              <div className="absolute -inset-4 bg-teal-200 rounded-full blur-3xl opacity-30 animate-pulse" data-id="element-35"></div>
              <img src="https://cdn.magicpatterns.com/uploads/23ATaiWoNbs8S9uD4RxgS9/01_cover_principal.png" alt="Kit Musicalización para niños completo" className="relative w-full max-w-md mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-36" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Copy Section */}
      <main className="max-w-4xl mx-auto px-4 py-16 space-y-16" data-id="element-37">
        {/* The Problem */}
        <section className="prose prose-lg prose-teal mx-auto text-gray-700" data-id="element-38">
          <p className="lead font-medium text-2xl text-teal-800" data-id="element-39">
            Hay algo que casi todos los adultos responsables de niños sienten,
            aunque no siempre lo dicen en voz alta.
          </p>
          <p data-id="element-40">
            Saben que la música es importante. Saben que aporta desarrollo
            cognitivo, emocional y social. Saben que despierta sensibilidad,
            atención y creatividad.
          </p>
          <p className="font-bold text-gray-900" data-id="element-41">
            Pero cuando llega el momento real de enseñarla, aparece el bloqueo.
          </p>
          <ul className="list-none pl-0 space-y-2 font-medium italic text-gray-600 border-l-4 border-orange-300 pl-6 my-8" data-id="element-42">
            <li data-id="element-43">¿Qué empiezo enseñando?</li>
            <li data-id="element-44">¿Esto será demasiado difícil?</li>
            <li data-id="element-45">¿Y si se aburren?</li>
            <li data-id="element-46">¿Y si yo no sé lo suficiente?</li>
          </ul>
          <p data-id="element-47">
            Y así, sin querer, la música queda postergada. No por falta de
            interés. Por falta de estructura.
          </p>
        </section>

        {/* The Core Issue */}
        <section className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-gray-100 text-center" data-id="element-48">
          <h2 className="text-3xl font-bold text-teal-900 mb-6" data-id="element-49">
            El problema no es la música. Es el método.
          </h2>
          <p className="text-lg mb-6" data-id="element-50">
            La mayoría de los recursos de educación musical infantil fallan por
            la misma razón:
          </p>
          <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-51">
            <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-52">
              <p className="text-red-800 font-medium" data-id="element-53">❌ Están fragmentados.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-54">
              <p className="text-red-800 font-medium" data-id="element-55">
                ❌ Son demasiado teóricos o demasiado sueltos.
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-56">
              <p className="text-red-800 font-medium" data-id="element-57">
                ❌ No construyen un progreso real.
              </p>
            </div>
          </div>
          <p className="mt-8 text-lg" data-id="element-58">
            El resultado es siempre parecido: Clases improvisadas. Actividades
            que no se conectan entre sí. Niños entusiasmados al principio… y
            desconectados después. Adultos cansados de "inventar" cada vez desde
            cero.
          </p>
          <p className="mt-4 font-bold text-xl text-orange-600" data-id="element-59">
            La música termina siendo un relleno, cuando en realidad debería ser
            una experiencia viva.
          </p>
        </section>

        {/* The Truth */}
        <section className="text-center space-y-6" data-id="element-60">
          <h2 className="text-3xl font-bold text-gray-900" data-id="element-61">
            La verdad que casi nadie dice
          </h2>
          <div className="bg-teal-900 text-white p-8 rounded-xl shadow-xl transform rotate-1 hover:rotate-0 transition-transform duration-300" data-id="element-62">
            <p className="text-2xl font-medium leading-relaxed" data-id="element-63">
              "Los niños no se aburren de la música. Se aburren de métodos sin
              dirección. Y los adultos no fallan por no saber música. Fallan
              porque nadie les dio un sistema claro para enseñarla."
            </p>
          </div>
        </section>

        {/* The Solution */}
        <section className="space-y-8" data-id="element-64">
          <h2 className="text-3xl font-bold text-teal-900 text-center" data-id="element-65">
            Qué cambia cuando existe una ruta clara
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-66">
            <div className="bg-white p-6 rounded-xl shadow-sm border-t-4 border-teal-500" data-id="element-67">
              <Check className="w-8 h-8 text-teal-500 mb-4" data-id="element-68" />
              <p className="text-lg" data-id="element-69">
                El adulto deja de improvisar. Las actividades tienen sentido
                entre sí.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border-t-4 border-teal-500" data-id="element-70">
              <Check className="w-8 h-8 text-teal-500 mb-4" data-id="element-71" />
              <p className="text-lg" data-id="element-72">
                Los niños entienden qué hacen y por qué lo hacen. El progreso se
                vuelve visible.
              </p>
            </div>
          </div>
          <p className="text-center text-2xl font-bold text-gray-800 mt-8" data-id="element-73">
            La música deja de ser "algo lindo" y pasa a ser algo concreto.
          </p>
          <p className="text-center text-xl text-gray-600" data-id="element-74">
            Eso es exactamente lo que hace el{' '}
            <span className="font-bold text-teal-700" data-id="element-75">
              Kit Musicalización para niños (6 Ebooks)
            </span>
            .
          </p>
        </section>

        {/* Product Showcase */}
        <section className="py-12" data-id="element-76">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-teal-900 mb-12" data-id="element-77">
            Qué incluye este sistema
          </h2>

          <div className="space-y-16" data-id="element-78">
            {/* Ebook 1 */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-79">
              <img src="https://cdn.magicpatterns.com/uploads/7zQudcfFfHDjKugtLspBg9/02_primeros_pasos_musica.png" alt="Primeros Pasos en la Música" className="w-full md:w-1/3 rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform" onClick={scrollToPurchase} data-id="element-80" />
              <div className="flex-1 space-y-4" data-id="element-81">
                <h3 className="text-2xl font-bold text-teal-800" data-id="element-82">
                  1. Primeros Pasos en la Música
                </h3>
                <p className="text-gray-600" data-id="element-83">
                  Introducción al lenguaje musical de forma simple y visual. Sin
                  pentagramas complejos al inicio, solo música real y accesible.
                </p>
                <ul className="space-y-2" data-id="element-84">
                  <li className="flex items-center gap-2" data-id="element-85">
                    <Music className="w-4 h-4 text-orange-500" data-id="element-86" /> Partituras y
                    prácticas
                  </li>
                  <li className="flex items-center gap-2" data-id="element-87">
                    <Music className="w-4 h-4 text-orange-500" data-id="element-88" /> Lenguaje
                    visual intuitivo
                  </li>
                </ul>
              </div>
            </div>

            {/* Ebook 2 */}
            <div className="flex flex-col md:flex-row-reverse items-center gap-8 bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-89">
              <img src="https://cdn.magicpatterns.com/uploads/c8tVAAkSc2PvHrhg72jPFy/03_Portada_juegos_sonoros.png" alt="Exploradores del Sonido" className="w-full md:w-1/3 rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform" onClick={scrollToPurchase} data-id="element-90" />
              <div className="flex-1 space-y-4" data-id="element-91">
                <h3 className="text-2xl font-bold text-teal-800" data-id="element-92">
                  2. Exploradores del Sonido
                </h3>
                <p className="text-gray-600" data-id="element-93">
                  Exploración sonora y rítmica mediante juegos guiados. La mejor
                  forma de aprender es jugando.
                </p>
                <ul className="space-y-2" data-id="element-94">
                  <li className="flex items-center gap-2" data-id="element-95">
                    <Music className="w-4 h-4 text-orange-500" data-id="element-96" /> Juegos sonoros
                  </li>
                  <li className="flex items-center gap-2" data-id="element-97">
                    <Music className="w-4 h-4 text-orange-500" data-id="element-98" /> Actividades
                    grupales e individuales
                  </li>
                </ul>
              </div>
            </div>

            {/* Ebook 3 */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-99">
              <img src="https://cdn.magicpatterns.com/uploads/7UzPmMQMKfvSTsVPV7huRv/04_Portada_Ejercicios_Musicales.png" alt="Ejercicios Musicales" className="w-full md:w-1/3 rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform" onClick={scrollToPurchase} data-id="element-100" />
              <div className="flex-1 space-y-4" data-id="element-101">
                <h3 className="text-2xl font-bold text-teal-800" data-id="element-102">
                  3. Ejercicios Musicales
                </h3>
                <p className="text-gray-600" data-id="element-103">
                  Actividades que conectan lo que se aprende con lo que se hace.
                  Práctica deliberada pero divertida.
                </p>
                <ul className="space-y-2" data-id="element-104">
                  <li className="flex items-center gap-2" data-id="element-105">
                    <Music className="w-4 h-4 text-orange-500" data-id="element-106" /> Actividades
                    para niños
                  </li>
                  <li className="flex items-center gap-2" data-id="element-107">
                    <Music className="w-4 h-4 text-orange-500" data-id="element-108" /> Refuerzo de
                    conceptos
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-orange-50 p-8 rounded-2xl border border-orange-100" data-id="element-109">
          <h2 className="text-3xl font-bold text-orange-800 mb-6" data-id="element-110">
            Cómo funciona el sistema
          </h2>
          <p className="mb-6" data-id="element-111">
            Cada ebook cumple una función dentro de una progresión pensada de
            principio a fin:
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-112">
            <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-113">
              <span className="font-bold text-orange-600 block mb-1" data-id="element-114">
                1. Introducción
              </span>
              Lenguaje musical simple y visual.
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-115">
              <span className="font-bold text-orange-600 block mb-1" data-id="element-116">
                2. Exploración
              </span>
              Juegos sonoros y rítmicos.
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-117">
              <span className="font-bold text-orange-600 block mb-1" data-id="element-118">
                3. Conexión
              </span>
              Actividades prácticas.
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-119">
              <span className="font-bold text-orange-600 block mb-1" data-id="element-120">
                4. Iniciación
              </span>
              Instrumentos reales.
            </div>
          </div>
          <p className="font-medium text-center italic" data-id="element-121">
            No hay saltos. No hay piezas aisladas. Hay continuidad.
          </p>
        </section>

        {/* Mid-page CTA */}
        <div className="text-center py-8" data-id="element-122">
          <button onClick={scrollToPurchase} className="bg-teal-600 hover:bg-teal-700 text-white text-xl font-bold py-4 px-10 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 inline-flex items-center gap-2" data-id="element-123">
            Quiero empezar hoy mismo
            <ArrowRight className="w-6 h-6" data-id="element-124" />
          </button>
        </div>

        {/* Target Audience */}
        <section className="grid md:grid-cols-2 gap-12 items-center" data-id="element-125">
          <div data-id="element-126">
            <h2 className="text-3xl font-bold text-teal-900 mb-6" data-id="element-127">
              Pensado para adultos reales
            </h2>
            <p className="mb-4" data-id="element-128">Este kit está pensado para:</p>
            <ul className="space-y-3" data-id="element-129">
              <li className="flex items-start gap-3" data-id="element-130">
                <Check className="w-6 h-6 text-teal-500 flex-shrink-0" data-id="element-131" />
                <span data-id="element-132">Docentes de nivel inicial y primario.</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-133">
                <Check className="w-6 h-6 text-teal-500 flex-shrink-0" data-id="element-134" />
                <span data-id="element-135">Talleristas sin formación musical formal.</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-136">
                <Check className="w-6 h-6 text-teal-500 flex-shrink-0" data-id="element-137" />
                <span data-id="element-138">Familias que quieren introducir música en casa.</span>
              </li>
            </ul>
            <p className="mt-6 font-medium text-gray-700 bg-teal-50 p-4 rounded-lg border-l-4 border-teal-500" data-id="element-139">
              Personas con poco tiempo, muchas responsabilidades y cero ganas de
              improvisar cada día.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100" data-id="element-140">
            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-141">Por eso:</h3>
            <ul className="space-y-4" data-id="element-142">
              <li className="flex items-center gap-3 font-bold text-teal-700 text-lg" data-id="element-143">
                <div className="bg-teal-100 p-1 rounded-full" data-id="element-144">
                  <Check className="w-5 h-5 text-teal-600" data-id="element-145" />
                </div>
                Todo está listo para usar
              </li>
              <li className="flex items-center gap-3 font-bold text-teal-700 text-lg" data-id="element-146">
                <div className="bg-teal-100 p-1 rounded-full" data-id="element-147">
                  <Check className="w-5 h-5 text-teal-600" data-id="element-148" />
                </div>
                Sistema paso a paso
              </li>
              <li className="flex items-center gap-3 font-bold text-teal-700 text-lg" data-id="element-149">
                <div className="bg-teal-100 p-1 rounded-full" data-id="element-150">
                  <Check className="w-5 h-5 text-teal-600" data-id="element-151" />
                </div>
                Solo seguir el método
              </li>
            </ul>
          </div>
        </section>

        {/* Benefits Summary */}
        <section className="bg-teal-900 text-white p-8 md:p-12 rounded-3xl text-center" data-id="element-152">
          <h2 className="text-3xl font-bold mb-8" data-id="element-153">Qué problemas resuelve</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6" data-id="element-154">
            <div className="bg-teal-800 p-4 rounded-xl" data-id="element-155">
              <ShieldCheck className="w-8 h-8 text-teal-300 mx-auto mb-3" data-id="element-156" />
              <p data-id="element-157">Elimina la improvisación constante</p>
            </div>
            <div className="bg-teal-800 p-4 rounded-xl" data-id="element-158">
              <ShieldCheck className="w-8 h-8 text-teal-300 mx-auto mb-3" data-id="element-159" />
              <p data-id="element-160">Reduce la inseguridad del adulto</p>
            </div>
            <div className="bg-teal-800 p-4 rounded-xl" data-id="element-161">
              <ShieldCheck className="w-8 h-8 text-teal-300 mx-auto mb-3" data-id="element-162" />
              <p data-id="element-163">Mantiene la motivación infantil</p>
            </div>
            <div className="bg-teal-800 p-4 rounded-xl" data-id="element-164">
              <ShieldCheck className="w-8 h-8 text-teal-300 mx-auto mb-3" data-id="element-165" />
              <p data-id="element-166">Convierte conceptos en experiencia</p>
            </div>
            <div className="bg-teal-800 p-4 rounded-xl sm:col-span-2 md:col-span-2" data-id="element-167">
              <ShieldCheck className="w-8 h-8 text-teal-300 mx-auto mb-3" data-id="element-168" />
              <p data-id="element-169">Ordena la enseñanza musical desde cero</p>
            </div>
          </div>
          <p className="mt-8 text-xl font-medium text-teal-200" data-id="element-170">
            No promete milagros. Promete proceso. Y en educación, eso es lo que
            realmente funciona.
          </p>
        </section>

        {/* Bonuses Section */}
        <section className="py-12" data-id="element-171">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-172">
            <Gift className="w-8 h-8 text-orange-500" data-id="element-173" />
            <h2 className="text-3xl font-bold text-gray-900" data-id="element-174">
              Bonos de Regalo
            </h2>
          </div>
          <div className="inline-block bg-orange-100 text-orange-800 px-4 py-1 rounded-full font-bold text-sm mb-8 mx-auto table" data-id="element-175">
            GRATIS HOY CON TU COMPRA
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-176">
            {/* Bonus 1 */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col" data-id="element-177">
              <div className="bg-gray-100 rounded-lg mb-4 overflow-hidden aspect-[3/4] relative group" data-id="element-178">
                <img src="https://cdn.magicpatterns.com/uploads/7y38VuZJTdLzhrLyqBC8rg/bono_teclado.png" alt="Bono Piano y Teclado" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-179" />
              </div>
              <h3 className="font-bold text-lg text-teal-800 mb-2" data-id="element-180">
                Explorando las Teclas
              </h3>
              <p className="text-sm text-gray-600" data-id="element-181">
                Piano y Teclado. Iniciación práctica al instrumento.
              </p>
            </div>

            {/* Bonus 2 */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col" data-id="element-182">
              <div className="bg-gray-100 rounded-lg mb-4 overflow-hidden aspect-[3/4] relative group" data-id="element-183">
                <img src="https://cdn.magicpatterns.com/uploads/wtPL77fxicNkK44YS7ztcs/bono_guitarra.png" alt="Bono Guitarra y Ukelele" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-184" />
              </div>
              <h3 className="font-bold text-lg text-teal-800 mb-2" data-id="element-185">
                Explorando las Cuerdas
              </h3>
              <p className="text-sm text-gray-600" data-id="element-186">
                Guitarra y Ukelele. Primeros acordes y melodías.
              </p>
            </div>

            {/* Bonus 3 */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col" data-id="element-187">
              <div className="bg-gray-100 rounded-lg mb-4 overflow-hidden aspect-[3/4] relative group" data-id="element-188">
                <img src="https://cdn.magicpatterns.com/uploads/cdERZeGycZkB2DxYWggcLh/bono_flauta.png" alt="Bono Flauta Dulce" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-189" />
              </div>
              <h3 className="font-bold text-lg text-teal-800 mb-2" data-id="element-190">
                Explorando los Vientos
              </h3>
              <p className="text-sm text-gray-600" data-id="element-191">
                Flauta Dulce. Respiración y digitación básica.
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12" data-id="element-192">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-193">
            Lo que dicen quienes ya lo usan
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-194">
            {testimonials.map((t, i) => <div key={i} className={i >= 3 ? 'md:col-span-1 lg:col-span-1' : ''} data-id="element-195">
                <TestimonialCard {...t} data-id="element-196" />
              </div>)}
          </div>
        </section>

        {/* Purchase Section */}
        <section id="purchase-section" className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-orange-100 max-w-3xl mx-auto" data-id="element-197">
          <div className="bg-orange-500 p-6 text-center text-white" data-id="element-198">
            <h2 className="text-3xl font-bold" data-id="element-199">Accedé al Kit Completo Hoy</h2>
            <p className="opacity-90 mt-2" data-id="element-200">
              Oferta especial por tiempo limitado
            </p>
          </div>

          <div className="p-8 md:p-12 text-center space-y-8" data-id="element-201">
            <div className="space-y-4" data-id="element-202">
              <p className="text-gray-500 line-through text-xl" data-id="element-203">
                Precio Regular: $39.990
              </p>
              <div className="flex items-center justify-center gap-2" data-id="element-204">
                <span className="text-6xl font-extrabold text-teal-900" data-id="element-205">
                  $19.990
                </span>
                <span className="text-xl text-gray-500 font-medium" data-id="element-206">ARS</span>
              </div>
            </div>

            <div className="space-y-4" data-id="element-207">
              <a href="https://turecurso.shop/cart/51610899251431:1" className="block w-full bg-orange-500 hover:bg-orange-600 text-white text-2xl font-bold py-6 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 animate-pulse" data-id="element-208">
                COMPRAR AHORA
              </a>
              <p className="text-sm text-gray-500 flex items-center justify-center gap-2" data-id="element-209">
                <ShieldCheck className="w-4 h-4" data-id="element-210" /> Pago 100% Seguro y
                Garantizado
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-left text-sm text-gray-600 pt-8 border-t border-gray-100" data-id="element-211">
              <div className="flex items-center gap-2" data-id="element-212">
                <Check className="w-4 h-4 text-teal-500" data-id="element-213" /> Acceso Inmediato
              </div>
              <div className="flex items-center gap-2" data-id="element-214">
                <Check className="w-4 h-4 text-teal-500" data-id="element-215" /> 6 Ebooks en PDF
              </div>
              <div className="flex items-center gap-2" data-id="element-216">
                <Check className="w-4 h-4 text-teal-500" data-id="element-217" /> Material Imprimible
              </div>
              <div className="flex items-center gap-2" data-id="element-218">
                <Check className="w-4 h-4 text-teal-500" data-id="element-219" /> Soporte por Email
              </div>
            </div>
          </div>
        </section>

        {/* Final FAQ/Closing */}
        <section className="text-center space-y-8 max-w-2xl mx-auto pt-8" data-id="element-220">
          <h3 className="text-2xl font-bold text-gray-900" data-id="element-221">
            La decisión es tuya
          </h3>
          <p className="text-gray-700" data-id="element-222">
            Si querés introducir la música en la vida de los niños de forma
            progresiva, práctica y aplicable desde el primer día, este sistema
            fue diseñado exactamente para eso.
          </p>
          <p className="font-bold text-teal-800 text-lg" data-id="element-223">
            No para lucirse. Para funcionar.
          </p>
        </section>
      </main>

      <footer className="bg-gray-900 text-gray-400 py-12 text-center" data-id="element-224">
        <div className="max-w-4xl mx-auto px-4" data-id="element-225">
          <p className="mb-4" data-id="element-226">
            &copy; 2026 Tu Recurso. Todos los derechos reservados.
          </p>
          <p className="text-sm" data-id="element-227">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio no está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>;
}